import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { ReviewsList } from "../components/ReviewForm";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import {
  Star, MapPin, DollarSign, Loader2, Shield, FileCheck, Clock, CheckCircle2,
  ArrowLeft, Image, Fingerprint
} from "lucide-react";

export default function PublicProfilePage() {
  const { professionalId } = useParams();
  const [professional, setProfessional] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await axios.get(`${API}/professional/${professionalId}/profile`);
        setProfessional(response.data);
      } catch (error) {
        toast.error("Failed to load professional profile");
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, [professionalId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-sand flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-forest" />
      </div>
    );
  }

  if (!professional) {
    return (
      <div className="min-h-screen bg-sand">
        <Header />
        <main className="pt-24 pb-12 px-4 md:px-8 max-w-4xl mx-auto text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Professional Not Found</h1>
          <Link to="/" className="text-forest hover:underline">Go back home</Link>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-sand">
      <Header />
      
      <main className="pt-24 pb-12 px-4 md:px-8 max-w-4xl mx-auto" data-testid="public-profile-page">
        <Link to="/professionals" className="flex items-center text-gray-600 hover:text-gray-900 mb-6">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Professionals
        </Link>

        {/* Profile Header */}
        <Card className="border-stone-200 mb-6">
          <CardContent className="pt-6">
            <div className="flex flex-col md:flex-row md:items-start gap-6">
              <div className="w-24 h-24 rounded-full bg-gradient-to-br from-forest to-forest-dark flex items-center justify-center text-white text-3xl font-bold shrink-0">
                {professional.full_name?.charAt(0) || "P"}
              </div>
              
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap mb-2">
                  <h1 className="font-heading font-bold text-2xl text-gray-900">
                    {professional.business_name || professional.full_name}
                  </h1>
                  {professional.is_verified && (
                    <Badge className="bg-blue-100 text-blue-700">
                      <Shield className="w-3 h-3 mr-1" /> Verified
                    </Badge>
                  )}
                  {professional.identity_verified && (
                    <Badge className="bg-emerald-100 text-emerald-700" data-testid="identity-verified-badge">
                      <Fingerprint className="w-3 h-3 mr-1" /> ID Verified
                    </Badge>
                  )}
                  {professional.background_check_status === "approved" && (
                    <Badge className="bg-purple-100 text-purple-700">
                      <FileCheck className="w-3 h-3 mr-1" /> Background Checked
                    </Badge>
                  )}
                </div>
                
                <p className="text-gray-600 mb-4">{professional.full_name}</p>
                
                {/* Rating */}
                {professional.average_rating && (
                  <div className="flex items-center gap-2 mb-4">
                    <div className="flex items-center gap-1">
                      <Star className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                      <span className="font-semibold text-lg">{professional.average_rating}</span>
                    </div>
                    <span className="text-gray-500">
                      ({professional.total_reviews} review{professional.total_reviews !== 1 ? "s" : ""})
                    </span>
                    {professional.average_quality && (
                      <span className="text-sm text-gray-400 hidden md:inline">
                        | Quality: {professional.average_quality} | Communication: {professional.average_communication} | Value: {professional.average_value}
                      </span>
                    )}
                  </div>
                )}
                
                {/* Location */}
                {professional.service_area && (
                  <div className="flex items-center gap-2 text-gray-600 mb-2">
                    <MapPin className="w-4 h-4" />
                    <span>{professional.service_area}</span>
                  </div>
                )}
                
                {/* Pricing */}
                {(professional.hourly_rate || professional.price_range_min) && (
                  <div className="flex items-center gap-4 text-gray-600 mb-4">
                    <DollarSign className="w-4 h-4" />
                    {professional.hourly_rate && (
                      <span className="font-medium">${professional.hourly_rate}/hr</span>
                    )}
                    {professional.price_range_min && professional.price_range_max && (
                      <span className="font-medium">
                        ${professional.price_range_min} - ${professional.price_range_max}
                      </span>
                    )}
                  </div>
                )}
                
                {/* Categories */}
                {professional.categories && professional.categories.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {professional.categories.map((cat) => (
                      <Badge key={cat} variant="secondary" className="bg-gray-100">
                        {cat}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Bio */}
        {professional.bio && (
          <Card className="border-stone-200 mb-6">
            <CardContent className="pt-6">
              <h2 className="font-heading font-semibold text-lg text-gray-900 mb-3">About</h2>
              <p className="text-gray-600 whitespace-pre-line">{professional.bio}</p>
            </CardContent>
          </Card>
        )}

        {/* Portfolio Images */}
        {professional.portfolio_images && professional.portfolio_images.length > 0 && (
          <Card className="border-stone-200 mb-6">
            <CardContent className="pt-6">
              <h2 className="font-heading font-semibold text-lg text-gray-900 mb-4 flex items-center gap-2">
                <Image className="w-5 h-5 text-blue-600" />
                Portfolio
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {professional.portfolio_images.map((img, index) => (
                  <button
                    key={`portfolio-${index}-${img.slice(-10)}`}
                    onClick={() => setSelectedImage(img)}
                    className="aspect-square rounded-lg overflow-hidden border-2 border-gray-200 hover:border-forest transition-colors"
                  >
                    <img 
                      src={img} 
                      alt={`Portfolio ${index + 1}`} 
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Reviews */}
        <Card className="border-stone-200">
          <CardContent className="pt-6">
            <h2 className="font-heading font-semibold text-lg text-gray-900 mb-4 flex items-center gap-2">
              <Star className="w-5 h-5 text-yellow-500" />
              Reviews ({professional.total_reviews || 0})
            </h2>
            <ReviewsList reviews={professional.reviews} />
          </CardContent>
        </Card>
      </main>

      {/* Image Modal */}
      {selectedImage && (
        <div 
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
        >
          <img 
            src={selectedImage} 
            alt="Portfolio" 
            className="max-w-full max-h-full object-contain rounded-lg"
          />
        </div>
      )}

      <Footer />
    </div>
  );
}
